import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Box, FormControlLabel, Grid, Switch, Tooltip } from '@mui/material'
import AmendModal from 'Pages/BookingsPage/AmendModal/ViewAmendModal'
import useScreenWakeLock from 'hooks/useScreenWakeLock'
import { useCallback, useEffect, useRef, useState } from 'react'
import BookingDetails from './BookingDetails/BookingDetails'
import BookingsMap from './BookingsMap'
import Table from './BookingsTable/Table'
import ReallocateModal from './ReallocateModal/ReallocateModal'

const DETAILS_TRANSITION_MS = 400
const WAKE_LOCK_ENABLED_KEY = 'journeyStatus.wakeLockEnabled'
const TABLE_PANEL_WIDTH_KEY = 'journeyStatus.tablePanelWidth'
const TABLE_PANEL_MIN_WIDTH = 45
const NARROW_SCREEN_BREAKPOINT_PX = 1600
const TABLE_PANEL_DEFAULT_WIDTH_WIDE = 65
const TABLE_PANEL_DEFAULT_WIDTH_NARROW = 55
const TABLE_PANEL_MAX_WIDTH_WIDE = 65
const TABLE_PANEL_MAX_WIDTH_NARROW = 60

const getTablePanelMaxWidth = () => {
  if (typeof window === 'undefined') return TABLE_PANEL_MAX_WIDTH_WIDE
  return window.innerWidth < NARROW_SCREEN_BREAKPOINT_PX
    ? TABLE_PANEL_MAX_WIDTH_NARROW
    : TABLE_PANEL_MAX_WIDTH_WIDE
}

const getTablePanelDefaultWidth = () => {
  if (typeof window === 'undefined') return TABLE_PANEL_DEFAULT_WIDTH_WIDE
  return window.innerWidth < NARROW_SCREEN_BREAKPOINT_PX
    ? TABLE_PANEL_DEFAULT_WIDTH_NARROW
    : TABLE_PANEL_DEFAULT_WIDTH_WIDE
}

const clampTablePanelWidth = (value: number, maxWidth: number) =>
  Math.min(maxWidth, Math.max(TABLE_PANEL_MIN_WIDTH, value))

const readWakeLockEnabledPreference = () => {
  if (typeof window === 'undefined') return true
  try {
    const storedValue = window.localStorage.getItem(WAKE_LOCK_ENABLED_KEY)
    if (storedValue == null) return true
    return storedValue === 'true'
  } catch {
    return true
  }
}

const readTablePanelWidthPreference = () => {
  const maxWidth = getTablePanelMaxWidth()
  const fallback = getTablePanelDefaultWidth()
  if (typeof window === 'undefined') return fallback
  try {
    const storedValue = window.localStorage.getItem(TABLE_PANEL_WIDTH_KEY)
    if (storedValue == null) return fallback
    const parsed = Number(storedValue)
    if (!Number.isFinite(parsed)) return fallback
    return clampTablePanelWidth(parsed, maxWidth)
  } catch {
    return fallback
  }
}

export type BookingDetailsProps = {
  selectedJob?: any
  setSelectedJob?: (e: any) => void
  setIsReallocateModalOpen?: (e: boolean) => void
  setIsAmendModalOpen?: (e: boolean) => void
}

export function BookingStatusPage() {
  const [isWakeLockEnabled, setIsWakeLockEnabled] = useState<boolean>(() =>
    readWakeLockEnabledPreference(),
  )

  useScreenWakeLock(isWakeLockEnabled)

  const [pageData, setPageData] = useState(undefined)
  const [selectedJob, setSelectedJob] = useState<any>(null)
  const [detailsJob, setDetailsJob] = useState<any>(null)
  const [detailsPhase, setDetailsPhase] = useState<
    'hidden' | 'entering' | 'visible' | 'leaving'
  >('hidden')
  const [isReallocateModalOpen, setIsReallocateModalOpen] =
    useState<boolean>(false)
  const [isAmendModalOpen, setIsAmendModalOpen] = useState<boolean>(false)
  const [detailsHeight, setDetailsHeight] = useState(0)
  const [tablePanelWidth, setTablePanelWidth] = useState(
    readTablePanelWidthPreference,
  )
  const [tableLayoutRecalcToken, setTableLayoutRecalcToken] = useState(0)
  const [isResizingPanels, setIsResizingPanels] = useState(false)
  const tablePanelWidthRef = useRef(tablePanelWidth)
  const previewPanelWidthRef = useRef(tablePanelWidth)
  const resizeBoundsRef = useRef<{ left: number; width: number } | null>(null)
  const queuedResizePercentRef = useRef<number | null>(null)
  const resizeAnimationFrameRef = useRef<number | null>(null)
  const detailsRef = useRef<HTMLDivElement | null>(null)
  const transitionTimeoutRef = useRef<number | null>(null)
  const splitContainerRef = useRef<HTMLDivElement | null>(null)
  const panelHeight = { xs: '560px', lg: 'calc(100dvh - 112px)' }
  const isDetailsOverlayActive =
    Boolean(detailsJob) && detailsPhase !== 'hidden'

  const applyPreviewPanelWidth = useCallback((nextWidth: number) => {
    previewPanelWidthRef.current = nextWidth
    splitContainerRef.current?.style.setProperty(
      '--booking-status-table-width-preview',
      `${nextWidth}%`,
    )
  }, [])

  const handleResizeStart = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (typeof window === 'undefined') return
      if (window.matchMedia('(max-width:1199.95px)').matches) return
      const container = splitContainerRef.current
      if (!container) return

      event.preventDefault()
      const bounds = container.getBoundingClientRect()
      if (bounds.width <= 0) return

      tablePanelWidthRef.current = tablePanelWidth
      resizeBoundsRef.current = { left: bounds.left, width: bounds.width }
      applyPreviewPanelWidth(tablePanelWidth)
      setIsResizingPanels(true)
    },
    [applyPreviewPanelWidth, tablePanelWidth],
  )

  useEffect(() => {
    if (!isResizingPanels) return
    if (typeof window === 'undefined') return

    const handleMouseMove = (event: MouseEvent) => {
      const bounds = resizeBoundsRef.current
      if (!bounds) return
      if (bounds.width <= 0) return

      const nextPercent = ((event.clientX - bounds.left) / bounds.width) * 100
      const clampedPercent = clampTablePanelWidth(
        nextPercent,
        getTablePanelMaxWidth(),
      )

      queuedResizePercentRef.current = clampedPercent
      if (resizeAnimationFrameRef.current != null) return

      resizeAnimationFrameRef.current = window.requestAnimationFrame(() => {
        resizeAnimationFrameRef.current = null
        if (queuedResizePercentRef.current == null) return

        applyPreviewPanelWidth(queuedResizePercentRef.current)
        queuedResizePercentRef.current = null
      })
    }

    const handleMouseUp = () => {
      if (resizeAnimationFrameRef.current != null) {
        window.cancelAnimationFrame(resizeAnimationFrameRef.current)
        resizeAnimationFrameRef.current = null
      }

      if (queuedResizePercentRef.current != null) {
        applyPreviewPanelWidth(queuedResizePercentRef.current)
        queuedResizePercentRef.current = null
      }

      const finalWidth = previewPanelWidthRef.current
      tablePanelWidthRef.current = finalWidth
      splitContainerRef.current?.style.setProperty(
        '--booking-status-table-width',
        `${finalWidth}%`,
      )
      splitContainerRef.current?.style.removeProperty(
        '--booking-status-table-width-preview',
      )
      setTablePanelWidth(finalWidth)
      setTableLayoutRecalcToken((currentToken) => currentToken + 1)
      resizeBoundsRef.current = null
      setIsResizingPanels(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      if (resizeAnimationFrameRef.current != null) {
        window.cancelAnimationFrame(resizeAnimationFrameRef.current)
        resizeAnimationFrameRef.current = null
      }
      queuedResizePercentRef.current = null
      resizeBoundsRef.current = null
    }
  }, [applyPreviewPanelWidth, isResizingPanels])

  useEffect(() => {
    splitContainerRef.current?.style.setProperty(
      '--booking-status-table-width',
      `${tablePanelWidth}%`,
    )
    tablePanelWidthRef.current = tablePanelWidth
  }, [tablePanelWidth])

  useEffect(() => {
    if (!isResizingPanels || typeof document === 'undefined') {
      return
    }

    const previousUserSelect = document.body.style.userSelect
    const previousCursor = document.body.style.cursor

    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'

    return () => {
      document.body.style.userSelect = previousUserSelect
      document.body.style.cursor = previousCursor
    }
  }, [isResizingPanels])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(
        WAKE_LOCK_ENABLED_KEY,
        String(isWakeLockEnabled),
      )
    } catch {
      // Ignore persistence errors and keep in-memory toggle behavior.
    }
  }, [isWakeLockEnabled])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(TABLE_PANEL_WIDTH_KEY, String(tablePanelWidth))
    } catch {
      // Ignore persistence errors and keep in-memory layout.
    }
  }, [tablePanelWidth])

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current)
      transitionTimeoutRef.current = null
    }

    if (!selectedJob) {
      if (!detailsJob) {
        setDetailsPhase('hidden')
        setDetailsHeight(0)
        return
      }

      setDetailsPhase('leaving')
      transitionTimeoutRef.current = window.setTimeout(() => {
        setDetailsJob(null)
        setDetailsPhase('hidden')
        setDetailsHeight(0)
        transitionTimeoutRef.current = null
      }, DETAILS_TRANSITION_MS)

      return
    }

    if (!detailsJob) {
      setDetailsJob(selectedJob)
      setDetailsPhase('entering')

      window.requestAnimationFrame(() => {
        setDetailsPhase('visible')
      })
      return
    }

    if (detailsJob?.id === selectedJob?.id) {
      setDetailsJob(selectedJob)
      setDetailsPhase('visible')
      return
    }

    setDetailsPhase('leaving')
    transitionTimeoutRef.current = window.setTimeout(() => {
      setDetailsJob(selectedJob)
      setDetailsPhase('entering')
      transitionTimeoutRef.current = null

      window.requestAnimationFrame(() => {
        setDetailsPhase('visible')
      })
    }, DETAILS_TRANSITION_MS)
  }, [detailsJob, selectedJob])

  useEffect(() => {
    if (!detailsJob || !detailsRef.current) {
      setDetailsHeight(0)
      return
    }

    const detailsElement = detailsRef.current
    const resizeObserver = new ResizeObserver(() => {
      setDetailsHeight(detailsElement.offsetHeight)
    })

    setDetailsHeight(detailsElement.offsetHeight)
    resizeObserver.observe(detailsElement)

    return () => {
      resizeObserver.disconnect()
    }
  }, [detailsJob?.id])

  console.log('BookingStatusPage Amend: PageData: ', pageData)
  console.log('BookingStatusPage Amend: Selected Job Updated: ', selectedJob)
  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          width: '100%',
          minHeight: 'var(--app-vh)',
          paddingTop: '80px',
          paddingLeft: '20px',
          paddingRight: '20px',
          paddingBottom: '20px',
          alignItems: 'stretch',
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            minHeight: panelHeight,
          }}
        >
          <Box
            ref={splitContainerRef}
            sx={{
              width: '100%',
              minHeight: panelHeight,
              display: { xs: 'grid', lg: 'flex' },
              position: 'relative',
              gridTemplateColumns: '1fr',
              rowGap: { xs: 2, lg: 0 },
              alignItems: 'stretch',
            }}
          >
            <Box
              sx={{
                width: {
                  xs: '100%',
                  lg: `var(--booking-status-table-width, ${tablePanelWidth}%)`,
                },
                minHeight: panelHeight,
                display: 'flex',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: panelHeight,
                  borderRadius: '8px',
                  boxShadow: '0 12px 28px rgba(0, 0, 0, 0.14)',
                  overflow: 'visible',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: '#f8f8f8',
                  }}
                >
                  <Table
                    setPageData={setPageData}
                    setSelectedJob={setSelectedJob}
                    selectedJob={selectedJob}
                    isModalOpen={isAmendModalOpen || isReallocateModalOpen}
                    layoutRecalcToken={tableLayoutRecalcToken}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize table and map panels"
              onMouseDown={handleResizeStart}
              sx={{
                display: { xs: 'none', lg: 'flex' },
                width: '14px',
                minWidth: '14px',
                cursor: 'col-resize',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
                '&::before': {
                  content: '""',
                  display: 'block',
                  width: '4px',
                  height: '54px',
                  borderRadius: '999px',
                  backgroundColor: isResizingPanels
                    ? 'rgba(0, 38, 61, 0.48)'
                    : 'rgba(0, 38, 61, 0.24)',
                  transition: 'background-color 160ms ease',
                },
              }}
            />
            <Box
              aria-hidden
              sx={{
                display: {
                  xs: 'none',
                  lg: isResizingPanels ? 'block' : 'none',
                },
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `calc(var(--booking-status-table-width-preview, ${tablePanelWidth}%) - 1px)`,
                width: '2px',
                borderRadius: '2px',
                backgroundColor: 'rgba(0, 38, 61, 0.5)',
                zIndex: 4,
                pointerEvents: 'none',
              }}
            />

            <Box
              sx={{
                width: {
                  xs: '100%',
                  lg: `calc(100% - var(--booking-status-table-width, ${tablePanelWidth}%) - 14px)`,
                },
                minHeight: panelHeight,
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                position: 'relative',
                isolation: 'isolate',
                maxWidth: '100%',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  width: '100%',
                  height: panelHeight,
                  boxShadow: '0 12px 28px rgba(0, 0, 0, 0.14)',
                  overflow: 'visible',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <BookingsMap
                    data={pageData}
                    selectedJob={selectedJob}
                    setSelectedJob={setSelectedJob}
                    detailsOverlayHeight={detailsHeight}
                    detailsOverlayActive={isDetailsOverlayActive}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      zIndex: 1201,
                      backgroundColor: 'rgba(255, 255, 255, 0.92)',
                      borderRadius: '999px',
                      px: 1.25,
                      py: 0.25,
                      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.16)',
                      backdropFilter: 'blur(3px)',
                    }}
                  >
                    <FormControlLabel
                      sx={{
                        margin: 0,
                        '.MuiFormControlLabel-label': { marginLeft: 0.5 },
                      }}
                      control={
                        <Switch
                          size="small"
                          checked={isWakeLockEnabled}
                          onChange={(_event, checked) =>
                            setIsWakeLockEnabled(checked)
                          }
                          inputProps={{
                            'aria-label': 'Toggle screen wake lock',
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', height: '100%' }}>
                          <Tooltip
                            title={`Screen sleep status: ${
                              isWakeLockEnabled ? 'Keep awake' : 'Allow sleep'
                            }`}
                          >
                            {isWakeLockEnabled ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Box>
                  {detailsJob && (
                    <Box
                      ref={detailsRef}
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1200,
                        pointerEvents:
                          detailsPhase === 'leaving' ? 'none' : 'auto',
                        opacity:
                          detailsPhase === 'hidden' ||
                          detailsPhase === 'leaving'
                            ? 0
                            : 1,
                        transform:
                          detailsPhase === 'hidden' ||
                          detailsPhase === 'leaving'
                            ? 'translate3d(0, 28px, 0)'
                            : 'translate3d(0, 0, 0)',
                        transition: `opacity ${DETAILS_TRANSITION_MS}ms ease, transform ${DETAILS_TRANSITION_MS}ms ease`,
                        willChange: 'opacity, transform',
                        backfaceVisibility: 'hidden',
                        transformStyle: 'preserve-3d',
                        contain: 'paint',
                      }}
                    >
                      <BookingDetails
                        selectedJob={detailsJob}
                        setSelectedJob={setSelectedJob}
                        setIsReallocateModalOpen={setIsReallocateModalOpen}
                        setIsAmendModalOpen={setIsAmendModalOpen}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <ReallocateModal
        isReallocateModalOpen={isReallocateModalOpen}
        setIsReallocateModalOpen={setIsReallocateModalOpen}
        selectedJob={selectedJob}
        key={selectedJob?.id}
      />
      {selectedJob && (
        <AmendModal
          key={selectedJob?.bookingId}
          openViewEditModal={isAmendModalOpen}
          setOpenViewEditModal={setIsAmendModalOpen}
          selectedBookingId={selectedJob?.bookingId}
        />
      )}
    </>
  )
}

export default BookingStatusPage
